import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import {
  Clock,
  Sparkles,
  ArrowLeft,
  ExternalLink,
  Download,
  AlertCircle,
  CheckCircle2,
  Info,
  TrendingUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";

/* -------------------------------------------------------------------------- */
/* Tax Engine (Income-tax Act, 2025 — Tax Year 2026-27)                       */
/* -------------------------------------------------------------------------- */

// New Regime slabs (default under Income-tax Act, 2025) for FY 2026-27
const NEW_REGIME_SLABS = [
  { upto: 400000, rate: 0 },
  { upto: 800000, rate: 5 },
  { upto: 1200000, rate: 10 },
  { upto: 1600000, rate: 15 },
  { upto: 2000000, rate: 20 },
  { upto: 2400000, rate: 25 },
  { upto: Infinity, rate: 30 },
];

// Old Regime slabs (below age 60)
const OLD_REGIME_SLABS_BELOW_60 = [
  { upto: 250000, rate: 0 },
  { upto: 500000, rate: 5 },
  { upto: 1000000, rate: 20 },
  { upto: Infinity, rate: 30 },
];
const OLD_REGIME_SLABS_60_TO_79 = [
  { upto: 300000, rate: 0 },
  { upto: 500000, rate: 5 },
  { upto: 1000000, rate: 20 },
  { upto: Infinity, rate: 30 },
];
const OLD_REGIME_SLABS_80_PLUS = [
  { upto: 500000, rate: 0 },
  { upto: 1000000, rate: 20 },
  { upto: Infinity, rate: 30 },
];

const CESS_RATE = 0.04;
const NEW_REGIME_STANDARD_DEDUCTION = 75000;
const OLD_REGIME_STANDARD_DEDUCTION = 50000;
const NEW_REGIME_87A_LIMIT = 1200000;
const OLD_REGIME_87A_LIMIT = 500000;

// Capital gains special rates (Income-tax Act, 2025)
const LTCG_RATE = 12.5;         // Section 198 (legacy 112A) — equity LTCG @ 12.5%
const LTCG_EXEMPTION = 125000;  // ₹1.25L annual exemption on equity LTCG
const STCG_RATE = 20;           // Section 196 (legacy 111A) — equity STCG @ 20%

function calcSlabTax(taxable, slabs) {
  if (taxable <= 0) return 0;
  let tax = 0;
  let prev = 0;
  for (const s of slabs) {
    if (taxable > s.upto) {
      tax += ((s.upto - prev) * s.rate) / 100;
      prev = s.upto;
    } else {
      tax += ((taxable - prev) * s.rate) / 100;
      break;
    }
  }
  return Math.round(tax);
}

function computeNewRegime({ slabIncome, salaryIncome, ltcg, stcg }) {
  const stdDed = Math.min(salaryIncome, NEW_REGIME_STANDARD_DEDUCTION);
  const taxable = Math.max(0, slabIncome - stdDed);
  let slabTax = calcSlabTax(taxable, NEW_REGIME_SLABS);
  // Section 87A rebate applies to slab-rated income only (not CG special rates)
  // Section 156 (legacy 87A) — under Income-tax Act, 2025
  if (taxable <= NEW_REGIME_87A_LIMIT) slabTax = 0;
  const ltcgTax = Math.max(0, ltcg - LTCG_EXEMPTION) * (LTCG_RATE / 100);
  const stcgTax = stcg * (STCG_RATE / 100);
  const tax = Math.round(slabTax + ltcgTax + stcgTax);
  const cess = Math.round(tax * CESS_RATE);
  return { taxable, tax, cess, total: tax + cess, ltcgTax: Math.round(ltcgTax), stcgTax: Math.round(stcgTax) };
}

function computeOldRegime({ slabIncome, salaryIncome, deductions, age, ltcg, stcg }) {
  const stdDed = Math.min(salaryIncome, OLD_REGIME_STANDARD_DEDUCTION);
  const taxable = Math.max(0, slabIncome - stdDed - deductions);
  const slabs =
    age >= 80
      ? OLD_REGIME_SLABS_80_PLUS
      : age >= 60
      ? OLD_REGIME_SLABS_60_TO_79
      : OLD_REGIME_SLABS_BELOW_60;
  let slabTax = calcSlabTax(taxable, slabs);
  if (taxable <= OLD_REGIME_87A_LIMIT) slabTax = Math.max(0, slabTax - 12500);
  if (slabTax < 0) slabTax = 0;
  const ltcgTax = Math.max(0, ltcg - LTCG_EXEMPTION) * (LTCG_RATE / 100);
  const stcgTax = stcg * (STCG_RATE / 100);
  const tax = Math.round(slabTax + ltcgTax + stcgTax);
  const cess = Math.round(tax * CESS_RATE);
  return { taxable, tax, cess, total: tax + cess, ltcgTax: Math.round(ltcgTax), stcgTax: Math.round(stcgTax) };
}

const fmt = (n) =>
  n === 0 ? "₹0" : "₹" + Math.round(n).toLocaleString("en-IN");

/* -------------------------------------------------------------------------- */
/* Quarterly Installment Schedule (Section 408, legacy Sec 211)               */
/* -------------------------------------------------------------------------- */
const INSTALLMENTS = [
  { key: "q1", label: "Q1 Installment", pct: 15, due: new Date("2026-06-15T18:30:00Z") },
  { key: "q2", label: "Q2 Installment", pct: 45, due: new Date("2026-09-15T18:30:00Z") },
  { key: "q3", label: "Q3 Installment", pct: 75, due: new Date("2026-12-15T18:30:00Z") },
  { key: "q4", label: "Q4 Final Installment", pct: 100, due: new Date("2027-03-15T18:30:00Z") },
];

function statusOf(dueDate) {
  const now = Date.now();
  const d = dueDate.getTime();
  const daysAway = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (daysAway < -3) return { key: "passed", label: "Passed" };
  if (daysAway <= 30 && daysAway >= -3) return { key: "current", label: "Action Due" };
  return { key: "upcoming", label: "Upcoming" };
}

/* -------------------------------------------------------------------------- */
/* UI Component                                                                */
/* -------------------------------------------------------------------------- */
const NumInput = ({ id, value, onChange, placeholder, testid }) => (
  <Input
    id={id}
    type="number"
    inputMode="numeric"
    min="0"
    value={value}
    onChange={(e) => onChange(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
    placeholder={placeholder}
    data-testid={testid}
    className="bg-slate-950/60 border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-500/50 h-11"
  />
);

const num = (v) => (v === "" || v == null || isNaN(v) ? 0 : Number(v));

export default function AdvanceTaxPlannerPage() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  // Force dark mode for this page only (calculator is dark-first design).
  // Save current theme, force dark, restore on unmount.
  useEffect(() => {
    const previous = theme;
    if (previous !== "dark") {
      setTheme("dark");
    }
    return () => {
      if (previous !== "dark") {
        setTheme(previous);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Profile
  const [age, setAge] = useState(32);

  // Realized (1 Apr – 31 Aug 2026)
  const [rSalary, setRSalary] = useState("");
  const [rBiz, setRBiz] = useState("");
  const [rFO, setRFO] = useState("");
  const [rLTCG, setRLTCG] = useState("");
  const [rSTCG, setRSTCG] = useState("");
  const [rOther, setROther] = useState("");

  // Projected (1 Sep 2026 – 31 Mar 2027)
  const [pSalary, setPSalary] = useState("");
  const [pBiz, setPBiz] = useState("");
  const [pFO, setPFO] = useState("");
  const [pLTCG, setPLTCG] = useState("");
  const [pSTCG, setPSTCG] = useState("");
  const [pOther, setPOther] = useState("");

  // Deductions & credits
  const [deductions, setDeductions] = useState("");
  const [empNps124_1B, setEmpNps124_1B] = useState(""); // Employee NPS — Sec 124(1B), max ₹50k, Old Regime only
  const [nps80CCD2, setNps80CCD2] = useState("");
  const [tdsTillAug, setTdsTillAug] = useState("");
  const [tdsProjected, setTdsProjected] = useState("");
  const [q1Paid, setQ1Paid] = useState("");

  // Manual regime override (default auto)
  const [regimeOverride, setRegimeOverride] = useState("auto"); // "auto" | "new" | "old"

  /* ---------- Derived ---------- */
  const totals = useMemo(() => {
    const salary = num(rSalary) + num(pSalary);
    const biz = num(rBiz) + num(pBiz);       // Business/Professional
    const fo = num(rFO) + num(pFO);          // F&O (treated as business income @ slab)
    const ltcg = num(rLTCG) + num(pLTCG);    // LTCG (special rate 12.5%)
    const stcg = num(rSTCG) + num(pSTCG);    // STCG (special rate 20%)
    const other = num(rOther) + num(pOther); // Interest/Dividend @ slab
    // Slab-rated income only (excludes CG which have special rates)
    const slabIncome = salary + biz + fo + other;
    const grossIncome = slabIncome + ltcg + stcg;
    return { salary, biz, fo, ltcg, stcg, other, slabIncome, grossIncome };
  }, [rSalary, rBiz, rFO, rLTCG, rSTCG, rOther, pSalary, pBiz, pFO, pLTCG, pSTCG, pOther]);

  const newR = useMemo(
    () => computeNewRegime({
      slabIncome: Math.max(0, totals.slabIncome - num(nps80CCD2)),
      salaryIncome: totals.salary,
      ltcg: totals.ltcg,
      stcg: totals.stcg,
    }),
    [totals, nps80CCD2]
  );
  const oldR = useMemo(
    () => {
      // Sec 124(1B) — Employee NPS additional deduction capped at ₹50,000 (Old Regime only)
      const empNpsDed = Math.min(num(empNps124_1B), 50000);
      return computeOldRegime({
        slabIncome: Math.max(0, totals.slabIncome - num(nps80CCD2)),
        salaryIncome: totals.salary,
        deductions: num(deductions) + empNpsDed,
        age: num(age),
        ltcg: totals.ltcg,
        stcg: totals.stcg,
      });
    },
    [totals, deductions, empNps124_1B, age, nps80CCD2]
  );

  const preferred = newR.total <= oldR.total ? "new" : "old";
  const chosenKey = regimeOverride === "auto" ? preferred : regimeOverride;
  const chosen = chosenKey === "new" ? newR : oldR;
  const savedBySwitch = Math.abs(newR.total - oldR.total);

  // Net advance tax = gross tax (chosen) minus total TDS credits
  const grossTax = chosen.total;
  const totalTdsCredits = num(tdsTillAug) + num(tdsProjected);
  const netTaxLiability = Math.max(0, grossTax - totalTdsCredits);

  // Sec 403 — Senior citizen (age >= 60) with NO PGBP income (Biz or F&O)
  const sec403Applies = num(age) >= 60 && totals.biz === 0 && totals.fo === 0;
  // Sec 404 — Net liability < ₹10,000
  const sec404Applies = !sec403Applies && netTaxLiability < 10000;

  const liableForAdvanceTax = !sec403Applies && !sec404Applies && netTaxLiability >= 10000;

  const q1Amt = num(q1Paid);
  const installments = INSTALLMENTS.map((ins) => {
    const cumulativeReq = Math.round((netTaxLiability * ins.pct) / 100);
    const prevPct = ins.key === "q1" ? 0 : INSTALLMENTS[INSTALLMENTS.findIndex((x) => x.key === ins.key) - 1].pct;
    const prevReq = Math.round((netTaxLiability * prevPct) / 100);
    const dueThisQuarter = Math.max(0, cumulativeReq - prevReq);
    let netDueNow;
    if (ins.key === "q1") {
      netDueNow = Math.max(0, cumulativeReq - q1Amt);
    } else if (ins.key === "q2") {
      netDueNow = Math.max(0, cumulativeReq - q1Amt);
    } else {
      netDueNow = dueThisQuarter;
    }
    return {
      ...ins,
      cumulativeReq,
      dueThisQuarter,
      netDueNow,
      status: statusOf(ins.due),
    };
  });

  const progressPct = netTaxLiability === 0 ? 0 : Math.min(100, Math.round((q1Amt / netTaxLiability) * 100));

  /* ---------- Actions ---------- */
  const handleDownload = () => {
    const lines = [
      "TaxSetu.ai — Advance Tax Schedule (Tax Year 2026-27)",
      "=".repeat(56),
      `Regime chosen: ${chosenKey.toUpperCase()} Regime`,
      `Gross Total Income: ${fmt(totals.grossIncome)}`,
      `Gross Tax (incl. 4% Cess): ${fmt(grossTax)}`,
      `Less: TDS/TCS credit: ${fmt(totalTdsCredits)}`,
      `Net Advance Tax Liability: ${fmt(netTaxLiability)}`,
      "",
      "Quarterly Installments (Section 408, IT Act 2025):",
    ];
    installments.forEach((i) => {
      lines.push(
        `  ${i.label.padEnd(24)} Due ${i.due.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} — Cumulative ${i.pct}% = ${fmt(i.cumulativeReq)}`
      );
    });
    lines.push("", "Governed by Sections 403, 404, 408 (legacy 207, 208, 211) · interest u/s 425 (legacy 234C) of the Income-tax Act, 2025.");
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TaxSetu_AdvanceTax_Schedule_TY2026-27.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------- Render ---------- */
  return (
    <div
      className="min-h-screen mesh-gradient text-white"
      data-testid="advance-tax-page"
    >
      {/* Unified Header — nav + hero merged, non-sticky, no clutter */}
      <section className="px-4 sm:px-6 lg:px-8 pt-6 pb-6 max-w-6xl mx-auto">
        {/* Slim top row: Back button only — page is dark-optimised */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-medium transition-colors"
            data-testid="atp-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>

        {/* Hero — logo inline with title, natural aspect ratio */}
        <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
          <img
            src="/logo.png"
            alt="TaxSetu AI"
            className="h-11 sm:h-14 w-auto flex-shrink-0"
            data-testid="atp-brand"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-[24px] sm:text-4xl lg:text-5xl font-bold font-heading tracking-tight leading-[1.05]">
              <span className="text-white">Advance Tax Planner </span>
              <span className="bg-gradient-to-r from-cyan-300 to-emerald-300 bg-clip-text text-transparent">& Regime Optimizer</span>
            </h1>
            <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[12px]">
              <span className="text-slate-400">Tax Year 2026-27</span>
              <span className="text-slate-600">·</span>
              <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-300/90 border border-emerald-500/25 bg-emerald-500/10 rounded-full px-2.5 py-0.5">
                <Sparkles className="w-3 h-3" /> Income-tax Act, 2025
              </span>
            </div>
          </div>
        </div>

        {/* Q2 alert banner */}
        <div className="mt-6 flex items-start sm:items-center gap-3 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-orange-500/5 px-4 py-3">
          <Clock className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5 sm:mt-0 animate-pulse" />
          <p className="text-[13px] sm:text-sm text-amber-100">
            <span className="font-bold">Upcoming Due Date:</span> Q2 Advance Tax due <span className="font-bold text-white">15 September 2026</span> — cumulative 45% of net tax liability.
          </p>
        </div>
      </section>

      {/* Inputs + Results grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-6">

        {/* LEFT — Inputs */}
        <div className="space-y-5">

          {/* Section A: Profile */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5">
            <div className="text-[11px] uppercase tracking-widest text-cyan-300/80 font-bold mb-3">Section A · Basic Profile</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="age" className="text-slate-300 text-xs mb-1.5 block">Your Age</Label>
                <NumInput id="age" value={age} onChange={setAge} placeholder="32" testid="atp-age" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1.5 block">Residential Status</Label>
                <div className="h-11 flex items-center px-3 rounded-md border border-white/10 bg-slate-950/60 text-sm text-slate-200">
                  Resident Individual
                </div>
              </div>
            </div>
          </div>

          {/* Section B: Income Buckets — Accordion style */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5">
            <div className="text-[11px] uppercase tracking-widest text-cyan-300/80 font-bold mb-3">Section B · Income (FY 2026-27)</div>

            <Accordion type="multiple" defaultValue={["earned"]} className="space-y-3">
              {/* Earned till August */}
              <AccordionItem value="earned" className="border border-cyan-500/20 bg-cyan-500/[0.04] rounded-xl overflow-hidden" data-testid="atp-acc-earned">
                <AccordionTrigger className="px-4 py-3 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <div className="flex items-center gap-2.5 text-left flex-1">
                    <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-cyan-300" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-bold text-white leading-tight">Income Earned Upto August 2026</div>
                      <div className="text-[11px] text-slate-400 leading-tight">1 April 2026 – 31 August 2026 · from Form 26AS / AIS</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 pt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Salary received</Label><NumInput value={rSalary} onChange={setRSalary} placeholder="e.g. 400000" testid="atp-r-salary" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Business / Professional receipts</Label><NumInput value={rBiz} onChange={setRBiz} placeholder="0" testid="atp-r-biz" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">F&amp;O Profit (net)</Label><NumInput value={rFO} onChange={setRFO} placeholder="0" testid="atp-r-fo" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">LTCG realized <span className="text-emerald-300/80 text-[10px]">· Sec 198 · @ 12.5%</span></Label><NumInput value={rLTCG} onChange={setRLTCG} placeholder="0" testid="atp-r-ltcg" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">STCG realized <span className="text-amber-300/80 text-[10px]">· Sec 196 · @ 20%</span></Label><NumInput value={rSTCG} onChange={setRSTCG} placeholder="0" testid="atp-r-stcg" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Other income (FD, dividend)</Label><NumInput value={rOther} onChange={setROther} placeholder="0" testid="atp-r-other" /></div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Prospective Sep - Mar */}
              <AccordionItem value="prospective" className="border border-emerald-500/20 bg-emerald-500/[0.04] rounded-xl overflow-hidden" data-testid="atp-acc-prospective">
                <AccordionTrigger className="px-4 py-3 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <div className="flex items-center gap-2.5 text-left flex-1">
                    <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-emerald-300" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-bold text-white leading-tight">Income Prospective · Sep 2026 to March 2027</div>
                      <div className="text-[11px] text-slate-400 leading-tight">1 September 2026 – 31 March 2027 · estimated</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 pt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Anticipated salary</Label><NumInput value={pSalary} onChange={setPSalary} placeholder="e.g. 560000" testid="atp-p-salary" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Expected business revenue</Label><NumInput value={pBiz} onChange={setPBiz} placeholder="0" testid="atp-p-biz" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Estimated F&amp;O Profit</Label><NumInput value={pFO} onChange={setPFO} placeholder="0" testid="atp-p-fo" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Projected LTCG <span className="text-emerald-300/80 text-[10px]">· Sec 198 · @ 12.5%</span></Label><NumInput value={pLTCG} onChange={setPLTCG} placeholder="0" testid="atp-p-ltcg" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Projected STCG <span className="text-amber-300/80 text-[10px]">· Sec 196 · @ 20%</span></Label><NumInput value={pSTCG} onChange={setPSTCG} placeholder="0" testid="atp-p-stcg" /></div>
                    <div><Label className="text-slate-400 text-xs mb-1.5 block">Other income (future)</Label><NumInput value={pOther} onChange={setPOther} placeholder="0" testid="atp-p-other" /></div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Total Annual Income */}
            <div className="mt-4 flex items-center justify-between text-[13.5px] px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500/15 to-emerald-500/15 border border-cyan-400/30 shadow-inner">
              <span className="text-slate-200 font-semibold">Total Annual Income</span>
              <span className="font-extrabold text-white text-[16px]" data-testid="atp-gross-income">{fmt(totals.grossIncome)}</span>
            </div>
            <p className="mt-2 text-[10.5px] text-slate-500 leading-relaxed px-1">
              LTCG (equity) taxed @ 12.5% u/s 198 on gains above ₹1.25 L exemption · STCG (equity) @ 20% u/s 196 · F&amp;O treated as PGBP at slab rates
            </p>
          </div>

          {/* Section C: Deductions */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5">
            <div className="text-[11px] uppercase tracking-widest text-cyan-300/80 font-bold mb-3">Section C · Deductions</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Chapter VIII */}
              <div className="flex flex-col">
                <Label className="text-slate-300 text-xs mb-1.5 block h-4 leading-4">
                  Chapter VIII <span className="text-slate-500">(123, 126, etc.)</span>
                </Label>
                <NumInput value={deductions} onChange={setDeductions} placeholder="e.g. 150000" testid="atp-deductions" />
                <p className="mt-1.5 text-[10px] text-slate-500 leading-snug">
                  e.g. LIC, PPF, ELSS, Tuition Fees, Home-loan principal, Mediclaim
                </p>
              </div>

              {/* Employee NPS — Sec 124(1B) */}
              <div className="flex flex-col">
                <Label className="text-slate-300 text-xs mb-1.5 block h-4 leading-4">
                  Employee NPS <span className="text-slate-500">u/s 124(1B)</span>
                </Label>
                <NumInput value={empNps124_1B} onChange={setEmpNps124_1B} placeholder="e.g. 50000" testid="atp-emp-nps-124-1b" />
                <p className="mt-1.5 text-[10px] text-slate-500 leading-snug">
                  Additional ₹50,000 over Chapter VIII · Old Regime only
                </p>
              </div>

              {/* Employer NPS — Sec 124(2) */}
              <div className="flex flex-col">
                <Label className="text-slate-300 text-xs mb-1.5 block h-4 leading-4">
                  Employer NPS <span className="text-emerald-300/80">u/s 124(2)</span>
                </Label>
                <NumInput value={nps80CCD2} onChange={setNps80CCD2} placeholder="e.g. 90000" testid="atp-nps-80ccd2" />
                <p className="mt-1.5 text-[10px] text-emerald-300/80 leading-snug">
                  Allowed under BOTH regimes · up to 14% (govt) / 10% (others)
                </p>
              </div>
            </div>
            <p className="mt-4 text-[11px] text-slate-500 leading-relaxed border-t border-white/5 pt-3">
              Chapter VIII &amp; Section 124(1B) apply only under the <span className="text-slate-300 font-semibold">Old Regime</span>. Section 124(2) — employer NPS contribution — is allowed under <span className="text-emerald-300 font-semibold">both regimes</span>.
            </p>
          </div>

          {/* Section D: Tax Credits */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5" data-testid="atp-section-d">
            <div className="text-[11px] uppercase tracking-widest text-cyan-300/80 font-bold mb-3">Section D · Tax Credits</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-400 text-xs mb-1.5 block">TDS Deducted till August 2026</Label>
                <NumInput value={tdsTillAug} onChange={setTdsTillAug} placeholder="from Form 26AS" testid="atp-tds-till-aug" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs mb-1.5 block">TDS Estimated (Sep 2026 – Mar 2027)</Label>
                <NumInput value={tdsProjected} onChange={setTdsProjected} placeholder="future TDS" testid="atp-tds-projected" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs mb-1.5 block">Advance Tax Deposited <span className="text-slate-500 text-[10px]">Q1 by 15 Jun</span></Label>
                <NumInput value={q1Paid} onChange={setQ1Paid} placeholder="0" testid="atp-q1" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-[12.5px] px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10">
              <span className="text-slate-400">Total tax credits available</span>
              <span className="font-bold text-cyan-200" data-testid="atp-total-credits">{fmt(totalTdsCredits + num(q1Paid))}</span>
            </div>
          </div>
        </div>

        {/* RIGHT — Results */}
        <div className="space-y-5 lg:sticky lg:top-20 lg:self-start">

          {/* Regime Comparison */}
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-900/40 p-5 shadow-xl shadow-black/20">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-bold">Regime Verdict</div>
              <TrendingUp className="w-4 h-4 text-emerald-300" />
            </div>
            <div
              className={`rounded-xl p-3 text-center ${
                preferred === "new"
                  ? "bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-emerald-400/40"
                  : "bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/40"
              }`}
              data-testid="atp-regime-verdict"
            >
              <div className="text-[12px] text-slate-300">Recommended</div>
              <div className="text-xl font-extrabold mt-0.5">
                {preferred === "new" ? "New Regime saves you " : "Old Regime saves you "}
                <span className="text-white">{fmt(savedBySwitch)}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div className={`rounded-xl p-3 border ${preferred === "new" ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="text-[10.5px] uppercase text-slate-400 tracking-wider font-bold">New Regime</div>
                <div className="text-[13.5px] text-slate-300 mt-1">Taxable: <span className="text-white font-semibold">{fmt(newR.taxable)}</span></div>
                <div className="text-[13.5px] text-slate-300">Tax + Cess: <span className="text-white font-bold" data-testid="atp-new-total">{fmt(newR.total)}</span></div>
              </div>
              <div className={`rounded-xl p-3 border ${preferred === "old" ? "border-amber-500/40 bg-amber-500/5" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="text-[10.5px] uppercase text-slate-400 tracking-wider font-bold">Old Regime</div>
                <div className="text-[13.5px] text-slate-300 mt-1">Taxable: <span className="text-white font-semibold">{fmt(oldR.taxable)}</span></div>
                <div className="text-[13.5px] text-slate-300">Tax + Cess: <span className="text-white font-bold" data-testid="atp-old-total">{fmt(oldR.total)}</span></div>
              </div>
            </div>
            {/* Manual override */}
            <div className="mt-4 flex items-center gap-2 text-[11.5px]">
              <span className="text-slate-400">Compute using:</span>
              {["auto", "new", "old"].map((k) => (
                <button
                  key={k}
                  onClick={() => setRegimeOverride(k)}
                  className={`px-2.5 py-1 rounded-full border transition-colors ${
                    regimeOverride === k
                      ? "border-cyan-400/60 bg-cyan-500/15 text-cyan-200 font-semibold"
                      : "border-white/10 text-slate-400 hover:text-white"
                  }`}
                  data-testid={`atp-override-${k}`}
                >
                  {k === "auto" ? "Auto (best)" : k === "new" ? "New" : "Old"}
                </button>
              ))}
            </div>
          </div>

          {/* Exemption alerts OR Installments */}
          {sec403Applies ? (
            <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/[0.08] p-5" data-testid="atp-sec403-alert">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                <div>
                  <div className="text-emerald-200 font-bold text-[15px]">Exempt from Advance Tax · Section 403 <span className="text-emerald-300/70 text-[11px] font-normal">(legacy Sec 207)</span></div>
                  <p className="text-[13px] text-slate-300 mt-1.5 leading-relaxed">
                    As a Resident Senior Citizen (Age ≥ 60) with <span className="font-semibold">no business/professional income (PGBP)</span>, you are exempt from paying advance tax under <span className="font-semibold">Section 403 (legacy Sec 207)</span> of the Income-tax Act, 2025. You can settle your taxes via <span className="font-semibold">Self-Assessment</span> before filing your ITR.
                  </p>
                </div>
              </div>
            </div>
          ) : sec404Applies ? (
            <div className="rounded-2xl border border-cyan-500/40 bg-cyan-500/[0.08] p-5" data-testid="atp-sec404-alert">
              <div className="flex items-start gap-3">
                <Info className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                <div>
                  <div className="text-cyan-200 font-bold text-[15px]">Below ₹10,000 Threshold · Section 404 <span className="text-cyan-300/70 text-[11px] font-normal">(legacy Sec 208)</span></div>
                  <p className="text-[13px] text-slate-300 mt-1.5 leading-relaxed">
                    Your net advance tax liability of <span className="font-bold text-white">{fmt(netTaxLiability)}</span> is below the statutory <span className="font-semibold">₹10,000 threshold</span>. Advance tax is not applicable — settle via Self-Assessment before filing ITR.
                  </p>
                </div>
              </div>
            </div>
          ) : liableForAdvanceTax ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="atp-schedule">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-widest text-amber-300/80 font-bold">Quarterly Schedule · Sec 408 <span className="text-slate-500 normal-case tracking-normal">(legacy 211)</span></div>
                <span className="text-[11px] text-slate-400">Net liability: <span className="text-white font-bold">{fmt(netTaxLiability)}</span></span>
              </div>

              {/* progress bar */}
              <div className="mb-4">
                <div className="h-2 rounded-full bg-slate-800/80 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
                </div>
                <div className="mt-1 flex justify-between text-[10.5px] text-slate-400">
                  <span>Paid: {fmt(q1Amt)} ({progressPct}%)</span>
                  <span>Target: {fmt(netTaxLiability)}</span>
                </div>
              </div>

              <div className="space-y-2.5">
                {installments.map((i) => {
                  const isCurrent = i.status.key === "current";
                  const isPassed = i.status.key === "passed";
                  return (
                    <div
                      key={i.key}
                      className={`rounded-xl px-3.5 py-3 border ${
                        isCurrent
                          ? "border-amber-400/60 bg-amber-500/[0.10] shadow-md shadow-amber-500/10"
                          : isPassed
                          ? "border-white/10 bg-white/[0.02] opacity-80"
                          : "border-white/10 bg-white/[0.02]"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[13px] font-bold text-white">{i.label} <span className="text-slate-400 font-normal">· {i.pct}% cumulative</span></div>
                          <div className="text-[11px] text-slate-400">Due {i.due.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[13px] font-bold text-white">{fmt(i.netDueNow)}</div>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider ${
                            isCurrent ? "bg-amber-500/30 text-amber-200" :
                            isPassed ? "bg-slate-700/60 text-slate-400" :
                            "bg-white/5 text-slate-400"
                          }`}>{i.status.label}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 234C warning */}
              <div className="mt-4 rounded-xl border border-red-500/25 bg-red-500/[0.06] px-3.5 py-3">
                <div className="flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
                  <p className="text-[11.5px] text-red-100/90 leading-relaxed">
                    <span className="font-bold">Section 425 (legacy 234C):</span> If any installment is short-paid, interest @ 1% per month is levied on the deferred amount till the next due date. Pay on time to avoid penalty.
                  </p>
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href="https://eportal.incometax.gov.in/iec/foservices/#/e-pay-tax-prelogin/user-details"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="atp-pay-cta"
                  className="inline-flex items-center justify-center gap-2 h-11 rounded-lg text-[13px] font-bold text-white bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 shadow-lg shadow-cyan-500/25 hover:scale-[1.02] transition"
                >
                  <ExternalLink className="w-4 h-4" /> Pay via IT Portal
                </a>
                <button
                  onClick={handleDownload}
                  data-testid="atp-download-cta"
                  className="inline-flex items-center justify-center gap-2 h-11 rounded-lg text-[13px] font-bold border border-white/15 text-slate-100 hover:bg-white/[0.06] transition"
                >
                  <Download className="w-4 h-4" /> Download Schedule
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-center text-sm text-slate-400" data-testid="atp-empty">
              Enter your income details to see your advance-tax schedule.
            </div>
          )}

          <div className="text-center text-[10.5px] text-slate-500 pt-2">
            Governed by Sections 403, 404, 408 (legacy 207, 208, 211) · interest u/s 425 (legacy 234C) · Income-tax Act, 2025.
          </div>
        </div>
      </section>
    </div>
  );
}
